/*
 * 版权: Copyright (c) 2019 red
 * 
 * 文件: index.ts
 * 创建日期: Friday January 11th 2019
 * 作者: pao
 * 说明:
 * 1、设备基础服务
 */
import * as TCP from 'net';
import { addon, BaseAddon, RecallFunctionList, log, RecallFunction } from 'pao-aop';
import { bufferToBase64 } from '../buffer/index';
import { SocketServer } from '../net/socket';
import { ServiceInfo, BaseService } from '../base/index';

/**
 * TCP设备
 * @description 支持TCP/IP的设备
 */
@addon('SocketDevice', 'TCP设备', '支持TCP/IP的设备')
export class SocketDevice extends BaseAddon {
    /** 设备ID */
    deviceID?: string;

    /** 套接字 */
    socket?: TCP.Socket;

    /** 回调函数列表 */
    recallFunctionList?= new RecallFunctionList();
    /** 
     * 发送消息
     * @param message 消息
     * @param returnFunctionID 消息命令
     * @param timeout 超时
     * @param checkToSend 发送前的检查函数
     * @returns 命令返回Promise
     */
    sendMessage?<T>(
        message: string | Buffer,
        returnFunctionID: any = undefined,
        timeout: number = 3000,
        timeoutCheckBreak: number = 100): Promise<T> {
        this.sendDataToSocket!(message);
        return this.recallFunctionList!.createRecallFunction(
            returnFunctionID,
            timeout,
            timeoutCheckBreak);
    }

    /**
     * 发送数据
     * @param deviceID 设备ID
     * @param data 数据
     */
    sendDataToSocket?(data: string | Buffer) {
        if (typeof (data) === 'string') {
            this.socket!.write(Buffer.from(data));
        } else {
            this.socket!.write(data);
        }

        if (typeof (data) === 'string') {
            log('netThing', `发送数据, 设备ID=${this.deviceID}, 数据=${data}`);
        } else {
            log('netThing', `发送数据, 设备ID=${this.deviceID}, 数据=${bufferToBase64(data).substr(0, 20)}`);
        }
    }

    /**
     * 处理接收数据
     * @param data 数据
     */
    onReceivedData?(data?: Buffer) {
    }

    /**
     * 处理错误
     * @param error 错误
     */
    onError?(error: any) {
        /**
         * 返回错误给客户端
         */
        this.recallFunctionList!.rejectAll(error);
    }
}

/**
 * Socket设备服务
 * @description 提供Socket设备与云端通讯的服务
 * @author pao
 */
@addon('SocketDeviceServer', 'Socket设备服务', '提供Socket设备与云端通讯的服务')
export class SocketDeviceServer<T extends SocketDevice> extends SocketServer {
    /** 命令超时时间，默认为30秒 */
    public commandTimeout = 30000;

    /**
     * 设备服务列表
     */
    private deviceServices: ServiceInfo[];

    protected onCreateDevice?: () => T;

    protected onDeviceStart?: (device: T, socket: TCP.Socket) => void;

    start?() {
        super.start!();
    }

    public setDeviceService(deviceServices: ServiceInfo[]) {
        this.deviceServices = deviceServices;
    }

    /**
     * 连接建立
     * @param socket 套接
     */
    onConnected?(socket: TCP.Socket): void {
        let device = this.onCreateDevice!();

        // 首先获取设备ID
        device.socket = socket;

        socket.on('error', (error: Error) => {
            log('netThing', new Error(`连接${device.socket!.remoteAddress} : ${device.socket!.remotePort}异常: ${error}`));
        });

        socket.on('timeout', () => {
            log('netThing', new Error(`连接${device.socket!.remoteAddress} : ${device.socket!.remotePort}超时`));
        });

        socket.on('close', (had_error: boolean) => {
            log('netThing', `连接${device.socket!.remoteAddress} : ${device.socket!.remotePort}关闭`);
        });

        socket.on('data', (data: Buffer) => {
            try {
                log('netThing', `收到来自${device.socket!.remoteAddress} : ${device.socket!.remotePort}的数据: ${data}`);
                this.onReceivedData!(device, data);
            } catch (error) {
                log('netThing', new Error(`连接${device.socket!.remoteAddress} : ${device.socket!.remotePort}时发生异常: ${error}`));
            }
        });

        this.onDeviceStart!(device, socket);
    }

    /**
     * 接收数据处理事件
     * @param device 设备ID
     * @param data 数据
     */
    onReceivedData?(device: T, data?: Buffer) {
        return device.onReceivedData!(data);
    }

    /**
     * 异常时的处理
     * @param device 设备ID
     * @param error 错误
     */
    onError?(device: T, error?: any) {
        return device.onError!(error);
    }

    protected getDeviceByName(deviceName: string) {
        if (!this.deviceServices) {
            return undefined;
        }
        return this.deviceServices.find(service => {
            return service.serviceName === deviceName;
        });
    }
}

/**
 * 基础设备服务
 * @description 所有设备服务的基类，实现了物联网设备服务接口
 * @author pao
 */
@addon('BaseDeviceService', '基础设备服务', '所有设备服务的基类')
export class BaseDeviceService extends BaseService {
    /** 设备列表 */
    protected deviceList?= new Map<string, SocketDevice>();

    /** 
     * 连接设备
     * @param deviceID 设备ID
     */
    connectDevice?(newDevice: SocketDevice): void {
        let deviceID = newDevice.deviceID!;
        this.deviceList!.set(deviceID, newDevice);
        this.onDeviceConnected!(deviceID);
    }

    /** 
     * 断开设备 
     * @param had_error 是否有错误
     */
    disconnectDevice?(deviceID: string, had_error: boolean): Promise<void> {
        return new Promise((executor, reject) => {
            try {
                if (had_error) {
                    this.setErrorToDevice!(deviceID, new Error('连接异常关闭'));
                }
                this.onDeviceDisconnected!(deviceID);
                this.deviceList!.delete(deviceID);
            } catch (err) {
                reject(err);
            }
        });
    }

    /** 
     * 事件: 有错误发生
     * @param error 错误
     */
    setErrorToDevice?(deviceID: string, error: Error): void {
        this.onDeviceError!(deviceID, error);
        this.getDevice!(deviceID)!.recallFunctionList!.rejectAll!(error);
    }

    /**
     * 发送服务消息
     * @param message 消息
     * @param returnFunctionID 返回消息ID
     * @param timeout 超时（单位：毫秒，如果小于或等于0，表示不设置超时）
     * @param timeoutCheckBreak 超时检查时间间隔（单位：毫秒）
     */
    protected sendMessage?<T>(
        deviceID: string,
        message: string | Buffer,
        returnFunctionID: any = undefined,
        timeout: number = -1,
        timeoutCheckBreak: number = 100): Promise<T> {
        let device = this.getDevice!(deviceID);
        if (!device) {
            throw new Error(`找不到设备:${deviceID}`);

        }
        return device!.sendMessage!(message, returnFunctionID, timeout, timeoutCheckBreak);
    }

    /**
     * 根据设备ID获取设备
     * @param deviceID 设备ID
     */
    protected getDevice?(deviceID?: string): SocketDevice | undefined {
        return this.deviceList!.get(deviceID!);
    }

    /** 
     * 回调函数列表是否为空
     * @param deviceID 设备ID
     */
    protected isRecallFunctionEmpty?(deviceID: string) {
        return this.getDevice!(deviceID)!.recallFunctionList!.isEmpty!();
    }

    /** 
     * 获取回调函数
     * @param deviceID 设备ID
     * @param functionID 命令ID
     */
    protected getRecallFunction?(deviceID: string, functionID: any): RecallFunction {
        return this.getDevice!(deviceID)!.recallFunctionList!.getRecallFunction!(functionID);
    }

    /**
     * 回调函数
     * @param deviceID 设备ID
     * @param functionID 命令ID
     * @param returnData 返回数据
     */
    protected recallFunction?(deviceID: string, functionID: any, returnData: any = undefined) {
        this.getDevice!(deviceID)!.recallFunctionList!.recallFunction!(functionID, returnData);
    }

    /** 
     * 接收数据
     * @param deviceID 设备ID
     * @param data 数据
     */
    public receiveData?(deviceID: string, data: Buffer): void {
        this.onDataReceived!(deviceID, data);
    }

    /** 
     * AT命令到达
     * @param deviceID 设备ID
     * @param data 数据
     */
    protected onCommandReceived?(deviceID: string, data: string): void { }

    /** 
     * 数据到达
     * @param deviceID 设备ID
     * @param data 数据
     */
    protected onDataReceived?(deviceID: string, data: Buffer): void { }

    /**
     * 设备错误
     * @param deviceID 设备ID
     * @param data 数据
     */
    protected onDeviceError?(deviceID: string, error: Error): void { }

    /**
     * 事件: 设备连接
     * @param deviceID 设备ID
     */
    protected onDeviceConnected?(deviceID: string): void { }

    /**
     * 事件: 设备断开
     * @param deviceID 设备ID
     */
    protected onDeviceDisconnected?(deviceID: string): void { }
}